import { Dialog, Transition } from '@headlessui/react'
import React, { Fragment, useCallback } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import InputField from '../../components/Form/InputField'
import { useUser } from '../../context/UserContext'
import { graphql } from '../../gql'
import { useMutation } from 'urql'
import { zodResolver } from '@hookform/resolvers/zod'

interface AnalyticsParametersModalProps {
  isOpen: boolean
  onClose: () => void
  onUpdated: () => void
}

const Schema = z.object({
  wasteReductionPerCycle: z.coerce.number().min(0),
  co2SavedPerKiloWaste: z.coerce.number().min(0),
})

type Schema = z.infer<typeof Schema>

const PatchBusiness = graphql(`
  mutation PatchBusiness2($id: ID!, $input: PatchBusinessInput!) {
    patchBusiness(id: $id, input: $input) {
      business {
        id
        wasteReductionPerCycle
        co2ReductionPerKiloWaste
      }
    }
  }
`)

export const AnalyticsParametersModal: React.FC<AnalyticsParametersModalProps> =
  ({ isOpen, onClose, onUpdated }) => {
    const user = useUser()

    const {
      register,
      handleSubmit,
      formState: { errors },
    } = useForm<Schema>({
      defaultValues: {
        wasteReductionPerCycle: user?.business?.wasteReductionPerCycle,
        co2SavedPerKiloWaste: user?.business?.co2ReductionPerKiloWaste,
      },
      resolver: zodResolver(Schema),
    })

    const [, patchBusiness] = useMutation(PatchBusiness)

    const onSubmit = useCallback(
      (data: Schema) => {
        const businessId = user?.business?.id ?? ''

        if (!businessId) {
          console.error('No business ID')
          return
        }

        patchBusiness({
          id: businessId,
          input: {
            wasteReductionPerCycle: data.wasteReductionPerCycle,
            co2ReductionPerKiloWaste: data.co2SavedPerKiloWaste,
          },
        }).then(() => {
          onUpdated()
          onClose()
        })
      },
      [user?.business?.id, patchBusiness, onUpdated, onClose]
    )

    return (
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="sm:flex sm:items-start">
                      <div className="mt-3 text-center sm:mt-0 sm:text-left">
                        <Dialog.Title
                          as="h3"
                          className="text-lg font-medium leading-6 text-gray-900"
                        >
                          Set parameters to calculate waste and CO₂ reduction
                        </Dialog.Title>
                      </div>
                    </div>
                    <div className={'py-4 flex flex-col gap-4'}>
                      <InputField
                        autoFocus={true}
                        register={register}
                        error={errors.wasteReductionPerCycle}
                        name={'wasteReductionPerCycle'}
                        label={'Waste reduction per cycle'}
                        suffix={'kg'}
                      />
                      <InputField
                        register={register}
                        error={errors.co2SavedPerKiloWaste}
                        name={'co2SavedPerKiloWaste'}
                        label={'CO₂ saved per kilo of waste'}
                        suffix={'kCO₂'}
                      />
                    </div>
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <button
                        type="submit"
                        className="inline-flex w-full justify-center rounded-md border border-transparent bg-emerald-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                      >
                        Set parameters
                      </button>
                      <button
                        type="button"
                        className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                        onClick={onClose}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    )
  }
